"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.toFeatureCollection = exports.multiPolygonToPolygons = exports.getBounds = exports.featuresToFeatureCollection = exports.geometryToFeature = exports.geometriesToFeatureCollection = exports.union = void 0;
const bbox_1 = __importDefault(require("@turf/bbox"));
const union_1 = __importDefault(require("@turf/union"));
/**
 * Function to return union of all the features in a feature collection.
 * Can be used to get boundary of all the features
 * @param layer
 * @returns Feature - {GeoJSON.Feature}
 */
const union = (layer) => {
    if (!layer || !layer.features || !layer.features.length) {
        return null;
    }
    if (layer.features.length === 1) {
        return layer.features[0];
    }
    /**
     * Ensure that we are only combining valid shapes.
     */
    const validShapes = layer.features.filter((feature) => feature.geometry.type === "MultiPolygon" ||
        feature.geometry.type === "Polygon");
    return validShapes.reduce((accumulator, currentFeature) => {
        const mergedShapes = (0, union_1.default)(accumulator, currentFeature);
        if (
        /**
         * Happens if both shapes are empty
         */
        !mergedShapes) {
            return accumulator;
        }
        else {
            return mergedShapes;
        }
    }, validShapes[0]);
};
exports.union = union;
/**
 * Function to convert any Geometry[] to FeatureCollection
 * @param geometries {Geometry[]} to convert to FeatureCollection
 * @returns FeatureCollection - {FeatureCollection}
 */
const geometriesToFeatureCollection = (geometries) => {
    return {
        type: "FeatureCollection",
        features: geometries.map((geometry) => (0, exports.geometryToFeature)(geometry)),
    };
};
exports.geometriesToFeatureCollection = geometriesToFeatureCollection;
/**
 * Function to convert any Geometry to Feature
 * @param geometries {Geometry} to convert to Feature
 * @returns Feature - {Feature}
 */
const geometryToFeature = (geometry, id, properties) => {
    return {
        type: "Feature",
        geometry,
        id,
        properties: Object.assign({}, properties),
    };
};
exports.geometryToFeature = geometryToFeature;
/**
 * Function to convert any Feature[] to FeatureCollection
 * @param geometries {Feature[]} to convert to FeatureCollection
 * @returns FeatureCollection - {FeatureCollection}
 */
const featuresToFeatureCollection = (features) => {
    return {
        type: "FeatureCollection",
        features,
    };
};
exports.featuresToFeatureCollection = featuresToFeatureCollection;
/**
 * Function to get bounds from a `GeoJson`
 * Used for passing as the value for fitBounds prop in `FabricMap` / `FabricMapHost` components
 * @param geoJson `GeoJSON`
 * @returns [`FitBounds`](https://github.com/alex3165/react-mapbox-gl/blob/master/docs/API.md#component-properties)
 */
const getBounds = (geoJson) => {
    const [x1, y1, x2, y2] = (0, bbox_1.default)(geoJson);
    return [
        [x1, y1],
        [x2, y2],
    ];
};
exports.getBounds = getBounds;
/**
 * Function to convert multipolygon in Geometry to individual Polygons
 * @param geometry - {MultiPolygon} geometry
 * @return [geometry] - {Geometry[]} individual polygons in an array
 */
const multiPolygonToPolygons = (geometry) => {
    if (geometry.type === "MultiPolygon") {
        return geometry.coordinates.map((polyCoords) => (Object.assign(Object.assign({}, geometry), { type: "Polygon", coordinates: polyCoords })));
    }
    return [geometry];
};
exports.multiPolygonToPolygons = multiPolygonToPolygons;
/**
 * Function to convert any GeoJson structure to FeatureCollection and assign id's to features
 * @param geoJson {GeoJSON} to convert to FeatureCollection
 * @returns FeatureCollection - {FeatureCollection}
 */
const toFeatureCollection = (geoJson) => {
    switch (geoJson.type) {
        case "Point":
        case "MultiPoint":
        case "LineString":
        case "MultiLineString":
        case "Polygon":
        case "MultiPolygon": {
            return (0, exports.geometriesToFeatureCollection)([geoJson]);
        }
        case "GeometryCollection": {
            return (0, exports.geometriesToFeatureCollection)(geoJson.geometries);
        }
        case "Feature": {
            return (0, exports.featuresToFeatureCollection)([geoJson]);
        }
        case "FeatureCollection": {
            return geoJson;
        }
        default:
            throw Error("Does not match any geojson type");
    }
};
exports.toFeatureCollection = toFeatureCollection;
